import { useContext, useEffect, useRef, useState } from "react"
import { AccountContext } from "../../contexts/AccountContexts"
import { requestMember } from "../../requests/members_requests"
import { ContentContext } from "../../contexts/ContentContext"
import { loadingActive } from "../../components/Background"
import { useTranslation } from "react-i18next"
import { FileInputExtended, SelectInput, TextInput, TextLargeInput } from "../../components/Input-Components"
import { TurnstileComponent } from "../../components/CaptchaComponents"
import { MAX_FILE_SIZE, MAX_FILES } from "../../entries"
import { requestTicketMemberSubmit } from "../../requests/ticket_requests"
import { darkAndLightThemeDefault } from "../../components/ThemeComponents"
import { useNavigate } from "react-router-dom"
import { PageHeader } from "../../components/HeaderComponents"


export const UserCreateTicketPage = () => {
    const { t } = useTranslation('common')
    const { user, language } = useContext(AccountContext)
    const { servers, categories, openErrorModal, openBanErrorModal } = useContext(ContentContext)

    const [serverToCharacter, setServerToCharacter] = useState({})
    const [hasNoCharacters, setHasNoCharacters] = useState(true)

    const [ticketId, setTicketId] = useState(null)

    const [category, setCategory] = useState(null)
    const [server, setServer] = useState(null)
    const [characterName, setCharacterName] = useState("")
    const [violatersName, setViolatersName] = useState("")
    const [subject, setSubject] = useState("")
    const [description, setDescription] = useState("")
    const [files, setFiles] = useState([]);

    const [isValid, setIsValid] = useState(false);
    const isViolationCategory = categories[category]?.has_violaters_form || (categories[category]?.locales?.["en"] ? categories[category]?.locales?.["en"] : categories[category]?.title) === "Violating the rules of the chat" || (categories[category]?.locales?.["en"] ? categories[category]?.locales?.["en"] : categories[category]?.title) === "Reporting suspected cheaters or bots" || (categories[category]?.locales?.["en"] ? categories[category]?.locales?.["en"] : categories[category]?.title) === "Out-of-game trade"
    
    
    const [captchaToken, setCaptchaToken] = useState(null)
    const [resetCaptcha, setResetCaptcha] = useState(false)

    const confirmationRef = useRef()

    const navigator = useNavigate()

    const _setFiles = (f) => {
        f = f.filter((el) => {
            for (let i = 0; i < files.length; i++) {
                if (files[i].name === el.name && files[i].lastModified === el.lastModified) {
                    return false
                }
            }
            return true
        })
        const _files = [...f, ...files].slice(0, MAX_FILES)
        for (const file of _files) {
            if (file.size > MAX_FILE_SIZE) {
                const error = {
                    status: t('WARNING', { ns: "errors" }),
                    message: t('file_size_exceeded', { ns: "errors" })
                }
                openErrorModal(error)
                setFiles([])
                return
            }
        }
        setFiles(_files)
    }


    useEffect(() => {
        if (!ticketId &&
            (category && server) && (
                characterName !== "" &&
                subject !== "" &&
                description !== "" &&
                (isViolationCategory ? violatersName !== "" : true)
            )) {
            setIsValid(true);
        }
        else {
            setIsValid(false)
        }
        if (!isViolationCategory) {
            setViolatersName("")
        }
    }, [category, server, characterName, subject, description, violatersName, ticketId]);


    useEffect(() => {
        if (captchaToken) {
            setResetCaptcha(false)
        }
    }, [captchaToken])


    useEffect(() => {
        if (user?.class === "user" && Object.keys(servers).length > 0) {
            loadingActive(true)

            requestMember({})
                .then((data) => {
                    let serverToCharacter_ = {}
                    let serverData = Object.values(servers)
                    let serverNameToServer = {}
                    for (let i = 0; i < Object.keys(servers).length; i++) {
                        serverToCharacter_[serverData[i].id] = []
                        serverNameToServer[serverData[i].title.toLowerCase()] = serverData[i]
                    }
                    for (let i = 0; i < data.member.characters.length; i++) {
                        let character = data.member.characters[i]
                        let charServer = serverNameToServer[character.server.toLowerCase()]
                        serverToCharacter_[charServer.id].push(character)
                    }
                    setServerToCharacter(serverToCharacter_)
                    setHasNoCharacters(data.member.characters.length === 0)
                })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
                .finally(() => {
                    loadingActive(false)
                })
        }
    }, [user, servers])

    useEffect(() => {
        const compContainer = confirmationRef?.current;
        if (compContainer && ticketId) {
            setTimeout(() => {
                window.scrollTo(0, compContainer.offsetTop - 100);
            }, 50);
        }
    }, [ticketId])


    const handleSubmit = async (event) => {
        event.preventDefault();
        loadingActive(true)
        requestTicketMemberSubmit({
            category: category,
            server: server,
            characterName: characterName,
            violatersName: violatersName.trim(),
            subject: subject.trim(),
            description: description.trim(),
            files: files,
            captchaToken: captchaToken,
        })
            .then((data) => {
                if (data?.status === "WARNING") {
                    openBanErrorModal(data)
                }
                else {
                    setTicketId(data.ticket_id)
                }
            })
            .catch((error) => {
                console.error(error.status, error.message)
                if (error.message === "rate_limit_ticket_submit" || error.message === "too_many_active_tickets") {
                    error.status = t("WARNING")
                    openErrorModal(error)
                }
                else if (error.message === "user_banned") {
                    // error.status = t("WARNING")
                }
                else {
                    openErrorModal(error)
                }
            })
            .finally(() => {
                loadingActive(false)
                setResetCaptcha(true)
            })

    }

    const handleNavigateTicketHistory = () => {
        navigator("/member/ticket-history")
    }


    return (
        <div className="flex flex-col items-center gap-5  pb-10 px-[2px] min-h-screen">
            <PageHeader pageTitle={t("Create Ticket")} />
            {
                ticketId &&
                <div className="flex flex-col w-full mx-auto max-w-[1200px] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-xl shadow-sm pb-12 py-5 px-3 sm:p-5 gap-2" ref={confirmationRef}>
                    <span className="text-lg text-green-700 dark:text-green-300">{t('Ticket created successfully!')}</span>
                    <span>{t("You can keep track of your ticket in the 'Ticket History' page.")}</span>
                    <button className={`btn btn-sm max-w-32 ${darkAndLightThemeDefault}`} onClick={handleNavigateTicketHistory}>{t("Ticket History")}</button>

                    <span className="text-sm">{t('ticket_created_email')}</span>
                    <span className="text-lg text-slate-700 dark:text-slate-300">{user?.email}</span>
                </div>
            }
            {
                hasNoCharacters &&
                <form
                    className="flex flex-wrap items-center w-full mx-auto max-w-[1200px] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-xl shadow-sm pb-12 py-5 px-3 sm:p-5 gap-4"
                    onSubmit={handleSubmit}
                >
                    <div className="flex w-full px-2">
                        <label className="text-blue-900 dark:text-blue-500 font-semibold text-xl">
                            {t("Create Ticket")}
                        </label>
                    </div>
                    <div className="flex flex-wrap w-full px-2">
                        <span className="text-lg">{t("You need to have a character in the game in order to create a support ticket.")}</span>
                    </div>
                </form>

            }

            {
                !ticketId && !hasNoCharacters &&
                <form
                    className="flex flex-wrap items-center w-full mx-auto max-w-[1200px] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-xl shadow-sm pb-12 py-5 px-3 sm:p-5 gap-4"
                    onSubmit={handleSubmit}
                >
                    <div className="flex w-full px-2">
                        <label className="text-blue-900 dark:text-blue-600 font-semibold text-xl">
                            {t("Create Ticket")}
                        </label>
                    </div>
                    <div className="flex flex-wrap w-full">

                        <div className="flex md:flex-auto md:flex-grow-0 md:flex-shrink-0 md:w-1/2 w-full px-2 py-2">
                            <label className="form-control w-full">
                                <div className="label">
                                    <span className="label-text text-base font-semibold text-gray-800 dark:text-gray-300">
                                        {t("Server")}
                                    </span>
                                </div>
                                <select required className="select select-bordered select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" onChange={(e) => { setServer(e.target.value); }}>
                                    <option disabled selected className="">{t("Select server")}</option>
                                    {Object.values(servers).map((option, index) => (
                                        <option key={index} value={option.id}>{t(option.title)}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="flex md:flex-auto md:flex-grow-0 md:flex-shrink-0 md:w-1/2 w-full px-2 py-2">
                            <label className="form-control w-full">
                                <div className="label">
                                    <span className="label-text text-base font-semibold text-gray-800 dark:text-gray-300">
                                        {t("Character Name")}
                                    </span>
                                </div>
                                <select required disabled={server === null} className="select select-bordered disabled:bg-opacity-50 select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" onChange={(e) => { setCharacterName(e.target.value); }}>
                                    <option disabled selected className="">{t("Your In-Game Character Name")}</option>
                                    {
                                        server !== null &&
                                        serverToCharacter[server]?.map((option, i) => (
                                            <option key={i} value={option.characterName}>{t(option.characterName)}</option>
                                        ))}
                                </select>
                            </label>
                        </div>

                        <div className="flex flex-wrap w-full items-start gap-4 md:gap-0 py-2">

                            <div className="flex md:flex-auto md:flex-grow-0 md:flex-shrink-0 md:w-1/2 w-full px-2">
                                <label className="form-control w-full">
                                    <div className="label">
                                        <span className="label-text text-base font-semibold text-gray-800 dark:text-gray-300">
                                            {t("Category")}
                                        </span>
                                    </div>
                                    <select required className="select select-bordered select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" onChange={(e) => { setCategory(e.target.value); }}>
                                        <option disabled selected>{t("Select category")}</option>
                                        {Object.values(categories).filter((c) => !c.disabled).map((option, index) => (
                                            <option key={index} value={option.id}>{t(option?.locales?.[language] ? option?.locales?.[language] : option.title)}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {
                                isViolationCategory &&
                                <TextInput
                                    labelName={t("Violater's Character Name")}
                                    placeHolder={t("Violater's In-Game Character Name")}
                                    inputVariable={violatersName}
                                    inputSetVariable={setViolatersName}
                                    limit={9}
                                />
                            }
                        </div>
                    </div>

                    <TextInput
                        labelName={t("Subject")}
                        placeHolder={t("Enter subject")}
                        inputVariable={subject}
                        inputSetVariable={setSubject}
                        limit={100}
                    />
                    <TextLargeInput
                        labelName={t("Description")}
                        placeHolder={t("Describe the difficulty that you are facing.")}
                        inputVariable={description}
                        inputSetVariable={setDescription}
                        wFull={true}
                        limit={1000}
                    />
                    <div className="w-full">
                        <div className="flex md:flex-auto md:flex-grow-0 md:flex-shrink-0 md:w-1/2 w-full px-2">
                            <p className="font-semibold px-1">
                                {t("Attachments")} <small>({t("You can select multiple files")} ({t('max. 5')}))</small>
                            </p>
                        </div>
                        <FileInputExtended inputVariable={files} inputSetVariable={_setFiles} />
                    </div>
                    <div className="flex flex-col items-center w-full gap-2">
                        <div className="flex w-full justify-center mt-3">
                            <TurnstileComponent getToken={setCaptchaToken} reset={resetCaptcha} />
                        </div>
                        <button
                            type="submit"
                            disabled={!isValid}
                            className="btn bg-primary hover:bg-primary/90 border-gray-300 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-white font-semibold"
                        >
                            {t('Create Ticket')}
                        </button>
                    </div>
                </form>
            }
        </div>
    )
}